import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface MarkdownProps {
  children: string;
}

/**
 * Render user/system markdown content. XSS-safe by default —
 * react-markdown does not interpret raw HTML.
 *
 * Tailwind typography utilities style lists/blockquotes/tables.
 * Links open in a new tab so triagers don't navigate away from the
 * incident page.
 *
 * Inline `code` spans that contain a 32-character hex value (MSTP
 * configuration digest, MD5, etc.) are rendered as a compact
 * `12345678…` chip; hovering shows the full value in a tooltip and
 * clicking copies it to the clipboard. The full text stays in the
 * DOM so accessibility tools and selection still work.
 */
export function Markdown({ children }: MarkdownProps) {
  if (!children) return null;
  return (
    <TooltipProvider delayDuration={150}>
      <div className="text-sm leading-relaxed [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_code]:font-mono [&_code]:text-xs [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_pre]:bg-muted [&_pre]:p-3 [&_pre]:rounded-md [&_pre]:overflow-x-auto [&_pre>code]:bg-transparent [&_pre>code]:p-0 [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground [&_strong]:font-semibold">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children: linkContent }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sev-5 hover:underline"
              >
                {linkContent}
              </a>
            ),
            code: ({ children: codeContent, ...rest }) => {
              // ReactNode covers strings, numbers, fragments, etc. —
              // only strings are meaningful here (digest detection
              // regex). Non-string children pass through untouched.
              const text = typeof codeContent === 'string' ? codeContent : '';
              if (DIGEST_HEX_RE.test(text)) {
                return <DigestCode value={text} />;
              }
              return <code {...rest}>{codeContent}</code>;
            },
          }}
        >
          {children}
        </ReactMarkdown>
      </div>
    </TooltipProvider>
  );
}

// Exactly 32 ASCII hex chars — the standard MSTP configuration
// digest length (also MD5). Tighter than `^[0-9A-Fa-f]+$` so we
// don't auto-tooltip arbitrary short code snippets like `0x42`.
const DIGEST_HEX_RE = /^[0-9A-Fa-f]{32}$/;

function DigestCode({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const truncated = value.slice(0, 8);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => { setCopied(false); }, 1500);
    } catch {
      // Clipboard permission denied or unavailable — silently
      // ignore. The full value is still visible in the tooltip
      // and selectable via screen readers.
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => { void handleCopy(); }}
          aria-label={`Copy digest ${value}`}
          className="font-mono text-xs bg-muted px-1 py-0.5 rounded hover:bg-muted-foreground/10 cursor-pointer transition-colors"
        >
          {truncated}…
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="font-mono">
        <div className="text-xs">{value}</div>
        <div className="mt-1 text-[10px] text-muted-foreground font-sans">
          {copied ? '✓ copied to clipboard' : 'Click to copy'}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
